import React, { useEffect, useState } from "react";
import axios from "axios";
import * as constants from "../constants";
import { 
  Download, 
  FileText, 
  Image, 
  File, 
  Calendar,
  HardDrive,
  Info,
  ExternalLink,
  FolderOpen,
  FileImage
} from "lucide-react";

const Documents = (props) => {
  const { description, headingText } = props.options;
  const [state, setState] = useState({
    cardHeaderSettings: {
      headingText: headingText,
      description: description,
    },
    listData: undefined,
    isLoading: true,
  });

  useEffect(() => {
    const fetchDocuments = async () => {
      try {
        setState(prev => ({ ...prev, isLoading: true }));
        const response = await axios.get(
          constants.BASEURL +
            "Document/Objects/" +
            props.options.objectId +
            "?objectType=" +
            props?.options?.objectType
        );
        parseDocuments(response.data);
      } catch (error) {
        console.log(error);
        setState(prev => ({ ...prev, isLoading: false, listData: [] }));
      }
    };

    fetchDocuments();
  }, [props?.options?.objectId, props?.options?.objectType]);

  const getFileIcon = (docName) => {
    if (!docName) return { icon: File, color: "text-gray-500", bg: "bg-gray-100" };
    
    const extension = docName.split(".").pop()?.toUpperCase();
    
    switch (extension) {
      case "DOC":
      case "DOCX":
        return { icon: FileText, color: "text-blue-600", bg: "bg-blue-50" };
      case "PDF":
        return { icon: FileText, color: "text-red-600", bg: "bg-red-50" };
      case "PNG":
      case "JPG":
      case "JPEG":
      case "SVG":
      case "BMP":
      case "GIF":
        return { icon: FileImage, color: "text-purple-600", bg: "bg-purple-50" };
      case "XLS":
      case "XLSX":
      case "CSV":
        return { icon: FileText, color: "text-green-600", bg: "bg-green-50" };
      case "ZIP":
      case "RAR":
        return { icon: File, color: "text-amber-600", bg: "bg-amber-50" };
      default:
        return { icon: File, color: "text-gray-500", bg: "bg-gray-100" };
    }
  };

  const getFileExtension = (docName) => {
    if (!docName) return "";
    return docName.split(".").pop()?.toUpperCase() || "";
  };

  const parseDocuments = (listItem) => {
    const newListData = listItem.map((item) => ({
      ...item,
      controlId: item.documentID,
      fileInfo: getFileIcon(item.docName),
      extension: getFileExtension(item.docName),
    }));

    setState((prevState) => ({
      ...prevState,
      listData: newListData,
      isLoading: false,
    }));
  };

  // Loading Skeleton
  const LoadingSkeleton = () => (
    <div className="space-y-3">
      {[1, 2].map((i) => (
        <div key={i} className="animate-pulse">
          <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
            <div className="w-10 h-10 bg-gray-200 rounded-lg"></div>
            <div className="flex-1">
              <div className="h-4 w-3/4 bg-gray-200 rounded mb-2"></div>
              <div className="h-3 w-1/2 bg-gray-200 rounded"></div>
            </div>
            <div className="w-8 h-8 bg-gray-200 rounded-lg"></div>
          </div>
        </div>
      ))}
    </div>
  );

  // Empty State
  const EmptyState = () => (
    <div className="flex flex-col items-center justify-center py-8 text-center">
      <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mb-3">
        <FolderOpen className="w-6 h-6 text-gray-400" />
      </div>
      <p className="text-sm text-gray-500 font-medium">
        No {String(state?.cardHeaderSettings?.headingText || "").toLowerCase() === "resources" ? "resources" : "documents"} available
      </p>
      <p className="text-xs text-gray-400 mt-1">{String(state?.cardHeaderSettings?.headingText || "").toLowerCase() === "resources" ? "Resources" : "Documents"} will appear here when available</p>
    </div>
  );

  if (state.isLoading) {
    return <LoadingSkeleton />;
  }

  if (!state.listData?.length) {
    return <EmptyState />;
  }

  return (
    <div className="space-y-3">
      {state.listData.map((doc, index) => {
        const IconComponent = doc.fileInfo.icon;
        return (
          <div
            key={doc.controlId || index}
            className="group relative bg-white rounded-lg p-3 transition-all duration-200 border border-gray-200 hover:shadow-md"
          >
            <div className="flex items-start gap-3">
              {/* File Icon */}
              <div className={`w-10 h-10 ${doc.fileInfo.bg} rounded-lg flex items-center justify-center flex-shrink-0`}>
                <IconComponent className={`w-5 h-5 ${doc.fileInfo.color}`} />
              </div>

              {/* File Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    {/* File Name */}
                    <h4 className="text-sm font-medium text-gray-900 truncate pr-2" title={doc.docName}>
                      {doc.docName}
                    </h4>
                    
                    {/* Meta Info */}
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1">
                      {/* File Type Badge */}
                      <span className={`inline-flex items-center px-1.5 py-0.5 text-xs font-medium rounded ${doc.fileInfo.bg} ${doc.fileInfo.color}`}>
                        {doc.extension}
                      </span>
                      
                      {/* File Size */}
                      {doc.docSize && (
                        <span className="inline-flex items-center gap-1 text-xs text-gray-500">
                          <HardDrive className="w-3 h-3" />
                          {doc.docSize}
                        </span>
                      )}
                      
                      {/* Upload Date */}
                      {doc.uploadDate && (
                        <span className="inline-flex items-center gap-1 text-xs text-gray-500">
                          <Calendar className="w-3 h-3" />
                          {doc.uploadDate.trim()}
                        </span>
                      )}
                    </div>

                    {/* Details if available */}
                    {/* {doc.details && (
                      <p className="text-xs text-gray-500 mt-1.5 flex items-start gap-1">
                        <Info className="w-3 h-3 mt-0.5 flex-shrink-0" />
                        <span className="line-clamp-2">{doc.details}</span>
                      </p>
                    )} */}
                  </div>

                  {/* Download Button */}
                  <a
                    href={doc.downloadLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-shrink-0 w-8 h-8 bg-white hover:bg-indigo-50 border border-gray-200 hover:border-indigo-300 rounded-lg flex items-center justify-center transition-all duration-200 group/btn mt-1.5"
                    title="Download"
                  >
                    <Download className="w-4 h-4 text-gray-500 group-hover/btn:text-blue-600 transition-colors" />
                  </a>
                </div>
              </div>
            </div>
          </div>
        );
      })}
      
      {/* Documents Count Footer */}
      {state.listData.length > 0 && (
        <div className="pt-2 border-t border-gray-100">
          <p className="text-xs text-gray-400 text-center">
            {String(state?.cardHeaderSettings?.headingText || "").toLowerCase() === "resources"
              ? `${state.listData.length} ${state.listData.length === 1 ? "resource" : "resources"}`
              : `${state.listData.length} ${state.listData.length === 1 ? "document" : "documents"}`
            }{" "}
            available
          </p>
        </div>
      )}
    </div>
  );
};

export default Documents;
